/**
 * ChannelsGrid — вкладка «Каналы»: плитки подключений со статусом.
 *
 * Статус берётся из фактов в тенанте, а не из намерения: для Telegram
 * «подключён» = есть токен бота И проставлен вебхук. Токен без вебхука — это
 * ещё не связь с клиентом, и плитка честно об этом говорит.
 *
 * Серые плитки (`available=false`) — интеграции, которых в коде пока нет.
 * Показываем, чтобы было видно куда расти и куда идти, когда клиент попросит
 * ещё канал, но не притворяемся, что их можно включить сегодня.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, Loader2, Plug } from 'lucide-react'
import { channelsApi, type ChannelCatalogItem } from '@/api/client'
import { ReplyDelay } from '@/components/ReplyDelay'

const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

const ALL = 'Все'

export function ChannelsGrid({
  dealId,
  kind = 'channel',
}: {
  dealId: string
  /** Что показываем: где агент общается (channel) или куда отдаёт данные (integration). */
  kind?: 'channel' | 'integration'
}) {
  const [group, setGroup] = useState<string>(ALL)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['channels-catalog', dealId],
    queryFn: async () => {
      if (USE_MOCK) return { tenant_id: null, channels: [] as ChannelCatalogItem[] }
      return channelsApi.catalog(dealId)
    },
    staleTime: 30 * 1000,
  })

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаю…
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Не удалось загрузить список.
      </div>
    )
  }

  const channels = (data.channels ?? []).filter((c) => (c.kind ?? 'channel') === kind)
  const groups = [ALL, ...Array.from(new Set(channels.map((c) => c.group)))]
  const shown = group === ALL ? channels : channels.filter((c) => c.group === group)
  const connected = channels.filter((c) => c.connected).length

  return (
    <div className="space-y-3">
      {/* Пауза — про то, КАК бот отвечает в живых каналах, поэтому здесь. */}
      {kind === 'channel' && <ReplyDelay dealId={dealId} />}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {groups.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroup(g)}
              className={`px-2.5 py-1 text-xs rounded border ${
                group === g ? 'bg-accent font-medium' : 'text-muted-foreground'
              }`}
            >
              {g}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">подключено {connected}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {shown.map((c) => (
          <div
            key={c.key}
            className={`rounded-lg border p-3 space-y-1.5 ${
              c.connected
                ? 'border-green-300 bg-green-50'
                : c.available
                  ? 'bg-card'
                  : 'bg-slate-50 opacity-70'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className={`text-sm font-medium ${c.available ? '' : 'text-muted-foreground'}`}>
                {c.name}
              </span>
              {c.connected ? (
                <span className="flex items-center gap-1 rounded-full bg-green-600 px-2 py-0.5 text-[11px] text-white shrink-0">
                  <CheckCircle2 className="h-3 w-3" />
                  Подключён
                </span>
              ) : !c.available ? (
                <span className="rounded bg-slate-200 px-2 py-0.5 text-[11px] text-slate-600 shrink-0">
                  Недоступно
                </span>
              ) : (
                <span className="flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] text-muted-foreground shrink-0">
                  <Plug className="h-3 w-3" />
                  Не подключён
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
            <p className={`text-[11px] ${c.connected ? 'text-green-800' : 'text-slate-500'}`}>
              {c.detail}
            </p>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground">
        {kind === 'channel'
          ? 'Статус считается по настройкам клиента. Живую доставку проверяет кнопка «Автотест каналов» на этапе подключения — она реально дёргает Telegram.'
          : 'Серые карточки — интеграции, которых в коде пока нет. Они показаны, чтобы было видно куда расти, и включить их сегодня нельзя.'}
      </p>
    </div>
  )
}
